// ─── WorkRoamer Trust & Transparency Sprint — Privacy Commitment Panel ────────
//
// Shown in SignupPage above the Terms checkbox.
// Communicates the platform's privacy commitment before the user agrees —
// making trust a product feature, not a legal checkbox.

import { Shield, Lock, UserCheck, Trash2, Download, Cpu } from 'lucide-react';
import { useLocation } from 'wouter';

const COMMITMENTS = [
  {
    icon: UserCheck,
    text: 'Your information belongs to you. We never sell your personal data.',
  },
  {
    icon: Lock,
    text: 'Your account is protected with industry-standard security: encrypted connections, secure password hashing, and session protection.',
  },
  {
    icon: Shield,
    text: 'Sensitive information is processed using secure, encrypted connections. Your data is never shared with third parties for advertising.',
  },
  {
    icon: Download,
    text: 'You can export all your data at any time — your mission history, knowledge, earnings records, and profile.',
  },
  {
    icon: Trash2,
    text: 'You can delete your account and all associated data at any time, with no questions asked.',
  },
  {
    icon: Cpu,
    text: 'AI features are designed to minimize the personal information sent to external providers. Credentials and sensitive patterns are stripped before every AI call.',
  },
];

export default function PrivacyCommitmentPanel() {
  const [, setLocation] = useLocation();

  return (
    <div style={{
      background: 'rgba(59,130,246,0.06)',
      border: '1px solid rgba(59,130,246,0.22)',
      borderRadius: 12,
      padding: '18px 20px',
      marginBottom: 16,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'rgba(59,130,246,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Lock size={15} color="#3b82f6" />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#e2eaf7', letterSpacing: '0.01em' }}>
            🔒 Your Privacy Matters
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>
            Before you continue, here's our commitment to you.
          </div>
        </div>
      </div>

      {/* Commitments */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {COMMITMENTS.map(({ icon: Icon, text }, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Icon size={13} color="#3b82f6" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.5 }}>
              {text}
            </span>
          </div>
        ))}
      </div>

      {/* Links */}
      <div style={{
        marginTop: 14, paddingTop: 12,
        borderTop: '1px solid rgba(59,130,246,0.15)',
        display: 'flex', gap: 14, flexWrap: 'wrap',
      }}>
        <button
          type="button"
          onClick={() => setLocation('/terms')}
          style={{
            background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', color: '#3b82f6', fontSize: 11,
            fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          Terms of Service →
        </button>
        <button
          type="button"
          onClick={() => setLocation('/privacy')}
          style={{
            background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', color: '#3b82f6', fontSize: 11,
            fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          Privacy Policy →
        </button>
        <button
          type="button"
          onClick={() => setLocation('/security-trust')}
          style={{
            background: 'none', border: 'none', padding: 0,
            cursor: 'pointer', color: '#3b82f6', fontSize: 11,
            fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2,
          }}
        >
          Security & Trust →
        </button>
      </div>
    </div>
  );
}
